import React, { Fragment, useEffect, useState } from "react";
import { connect } from "react-redux";
import { useNavigate, useLocation } from "react-router";
import { toast } from "react-toastify";
import Select from "react-select";
import { getSlide, saveSlide } from "../../../api/slide.service";
import { getContentArea } from "../../../api/contentArea.service";
import { getSubjectAllByContent } from "../../../api/subject.service";
import { getAllDomainsBySubject } from "../../../api/domain.service";
import { getStandardsByDomainId } from "../../../api/standard.service";
import { IBreadcrumb } from "../../../model/interface/breadcrumb";
import { ISlide } from "../../../model/interface/slide";
import { ISelectElement } from "../../../model/interface/keyValuePair";
import constant from "../../../utils/constant";
import Breadcrumb from "../../shared/common/breadcrumb";
import Loader from "../../shared/loader/loader";
import SlideStandards from "./slideStandards";
import AllowedTo from "../../shared/common/rbac";
import Permissions from "../../../utils/permissions";
import AccessDenied from "../../shared/common/accessDenied";
import { useNumericParam } from "../../../utils/useNumericParam";

const ManageSlide = (props: any) => {
  const defaultData: ISlide = {
    slideId: 0,
    standardId: 0,
    title: "",
    description: "",
    link: "",
    isActive: true,
  };
  const [slide, setSlide] = useState<ISlide>(defaultData);
  const [slideToEdit, setSlideToEdit] = useState<ISlide>();
  const [loading, setLoading] = useState<boolean>(false);
  const location = useLocation();
  const parameters = location.state as any;
  const navigate = useNavigate();
  const id = useNumericParam("id");
  const idToEdit = id ? id : 0;
  type tabName = "SlideDetails" | "Standards";
  const [activeTab, setActiveTab] = useState<tabName>("SlideDetails");
  const activeTabClass =
    "text-gray-900 topBorder bg-white border border-b-0 group relative min-w-0 flex-1 mb-0 overflow-hidden py-4 px-4 text-sm font-medium text-center hover:bg-gray-50 dark:hover:bg-gray-700 focus:z-10";
  const inActiveTabClass =
    "text-gray-500 hover:text-gray-700 group relative min-w-0 flex-1 overflow-hidden mb-0 py-4 px-4 text-sm font-medium text-center hover:bg-gray-50 focus:z-10";
  const [isSaving, setIsSaving] = useState<boolean>(false);

  // Cascading dropdown states
  const [selectedContent, setSelectedContent] = useState<ISelectElement>();
  const [selectedSubject, setSelectedSubject] = useState<ISelectElement>();
  const [selectedDomain, setSelectedDomain] = useState<ISelectElement>();
  const [selectedStandard, setSelectedStandard] = useState<ISelectElement>();
  const [contentAreas, setContentAreas] = useState<ISelectElement[]>([]);
  const [subjects, setSubjects] = useState<ISelectElement[]>([]);
  const [domains, setDomains] = useState<ISelectElement[]>([]);
  const [standards, setStandards] = useState<ISelectElement[]>([]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setLoading(true);
      try {
        if (parameters?.autoFillData && idToEdit === 0) {
          const standardData = parameters.autoFillData;
          setSlide((prev) => ({
            ...prev,
            standardId: standardData.standardId || 0,
            contentAreaId: standardData.contentAreaId || 0,
            contentAreaName: standardData.contentAreaName || "",
            subjectId: standardData.subjectId || 0,
            subjectName: standardData.subjectName || "",
            domainId: standardData.domainId || 0,
            domainName: standardData.domainName || "",
            standardName: standardData.standardName || standardData.name || "",
            standardCode: standardData.standardCode || standardData.code || "",
          }));
          setSlideToEdit({
            ...defaultData,
            standardId: standardData.standardId || 0,
            contentAreaId: standardData.contentAreaId || 0,
            contentAreaName: standardData.contentAreaName || "",
            subjectId: standardData.subjectId || 0,
            subjectName: standardData.subjectName || "",
            domainId: standardData.domainId || 0,
            domainName: standardData.domainName || "",
            standardName: standardData.standardName || standardData.name || "",
            standardCode: standardData.standardCode || standardData.code || "",
          });
        } else {
          setSlideToEdit(undefined);
        }

        await bindSlideToEdit();
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [idToEdit, location.pathname]);

  async function bindSlideToEdit() {
    if (idToEdit) {
      try {
        const response = await getSlide(idToEdit);
        const payload = response?.data?.data ?? response?.data;
        if (!payload) return;

        const slideEditData: ISlide = {
          slideId: payload.slideId,
          standardId: payload.standardId,
          standardName: payload.standardName,
          standardCode: payload.standardCode,
          title: payload.title,
          description: payload.description,
          link: payload.link,
          isActive: payload.isActive,
          contentAreaId: payload.contentAreaId,
          contentAreaName: payload.contentAreaName,
          subjectId: payload.subjectId,
          subjectName: payload.subjectName,
          gradeLevel: payload.gradeLevel,
          domainId: payload.domainId,
          domainName: payload.domainName,
        };

        setSlideToEdit(slideEditData);
        setSlide(slideEditData);

        const selectedContent: any = slideEditData
          ? {
              label: slideEditData.contentAreaName,
              value: slideEditData.contentAreaId,
            }
          : undefined;

        const selectedSubject: any = slideEditData
          ? {
              label: slideEditData.subjectName,
              value: slideEditData.subjectId,
            }
          : undefined;

        const selectedDomain: any = slideEditData
          ? {
              label: slideEditData.domainName,
              value: slideEditData.domainId,
            }
          : undefined;

        const selectedStandard: any = slideEditData
          ? {
              label:
                (slideEditData.standardCode || "") +
                " " +
                (slideEditData.standardName || ""),
              value: slideEditData.standardId,
            }
          : undefined;

        bindContentAreas(selectedContent);
        bindSubjects(selectedContent, selectedSubject?.value);
        bindDomains(selectedSubject, selectedDomain?.value);
        bindStandards(selectedDomain, selectedStandard?.value);
      } catch (error) {
        console.log(error);
        toast.error("Error loading slide");
      }
    } else {
      // For new slides, check if we have autoFillData
      if (parameters?.autoFillData) {
        const standardData = parameters.autoFillData;
        const selectedContent: any = standardData.contentAreaId
          ? {
              label: standardData.contentAreaName,
              value: standardData.contentAreaId,
            }
          : undefined;

        const selectedSubject: any = standardData.subjectId
          ? {
              label: standardData.subjectName,
              value: standardData.subjectId,
            }
          : undefined;

        const selectedDomain: any = standardData.domainId
          ? {
              label: standardData.domainName,
              value: standardData.domainId,
            }
          : undefined;

        const selectedStandard: any = standardData.standardId
          ? {
              label:
                (standardData.standardCode || standardData.code || "") +
                " " +
                (standardData.standardName || standardData.name || ""),
              value: standardData.standardId,
            }
          : undefined;

        bindContentAreas(selectedContent);
        if (selectedContent)
          bindSubjects(selectedContent, selectedSubject?.value);
        if (selectedSubject)
          bindDomains(selectedSubject, selectedDomain?.value);
        if (selectedDomain)
          bindStandards(selectedDomain, selectedStandard?.value);
      } else {
        bindContentAreas();
        bindSubjects();
        bindDomains();
        bindStandards();
      }
    }
  }

  async function bindContentAreas(selectedItem?: ISelectElement) {
    try {
      const response = await getContentArea();
      const result = response.data.data;
      const contentAreaData = result.map((item) => {
        return { label: item.name, value: item.contentAreaId };
      });
      setContentAreas(contentAreaData);
      if (selectedItem) {
        setSelectedContent(selectedItem);
      } else if (
        slideToEdit &&
        slideToEdit.contentAreaId &&
        slideToEdit.contentAreaId > 0
      ) {
        const selectedContentArea = contentAreaData.find((t) => {
          return t.value === slideToEdit.contentAreaId;
        });
        setSelectedContent(selectedContentArea);
      }
    } catch (error) {
      console.log(error);
    }
  }

  async function bindSubjects(
    selectedItem?: ISelectElement,
    subjectId?: number
  ) {
    const contentId =
      selectedItem?.value === undefined
        ? slideToEdit && slideToEdit.contentAreaId
        : parseInt(selectedItem.value);

    if (!contentId) return;

    try {
      const response = await getSubjectAllByContent(contentId);
      const result = response.data;
      const subjectsData = result.map((item) => {
        return { label: item.subjectName, value: item.subjectId };
      });

      setSubjects(subjectsData);
      const currentSubject = slideToEdit ? slideToEdit.subjectId : subjectId;
      if (currentSubject) {
        const selectedSubject = subjectsData.find((t) => {
          return t.value === currentSubject;
        });
        setSelectedSubject(selectedSubject);
      }
    } catch (error) {
      console.log(error);
    }
  }

  function bindDomains(selectedItem?: ISelectElement, domainId?: number) {
    const subjectId =
      selectedItem?.value === undefined
        ? slideToEdit && slideToEdit.subjectId
        : parseInt(selectedItem.value);

    if (!subjectId) return;

    getAllDomainsBySubject(subjectId).then((response) => {
      const result = response.data.data;
      const domainsData = result.map((item) => {
        return { label: item.name, value: item.domainId };
      });
      setDomains(domainsData);
      const currentDomain = slideToEdit
        ? slideToEdit.domainId
          ? slideToEdit.domainId
          : 0
        : domainId;
      if (currentDomain) {
        const selectedDomain = domainsData.find((t) => {
          return t.value === currentDomain;
        });
        setSelectedDomain(selectedDomain);
      }
    });
  }

  function bindStandards(selectedItem?: ISelectElement, standardId?: number) {
    const domainId =
      selectedItem?.value === undefined
        ? slideToEdit && slideToEdit.domainId
        : parseInt(selectedItem.value);

    if (!domainId) return;

    getStandardsByDomainId(domainId).then((response: any) => {
      if (response.data.data.length > 0) {
        const standardsData = response.data.data.map((standard) => {
          return {
            label: standard.code + " " + standard.name,
            value: standard.standardId,
          };
        });
        setStandards(standardsData);

        const currentStandard =
          selectedItem?.value === undefined
            ? slideToEdit && slideToEdit.standardId
            : standardId;

        if (currentStandard) {
          const selectedStandard = standardsData.find((t) => {
            return t.value === currentStandard;
          });
          if (selectedStandard) {
            setSelectedStandard(selectedStandard);
          }
        }
      }
    });
  }

  function handleContentAreaChange(selectedItem: any) {
    setSelectedContent(selectedItem);
    setSelectedSubject(undefined);
    setSelectedDomain(undefined);
    setSelectedStandard(undefined);
    setSubjects([]);
    setDomains([]);
    setStandards([]);
    bindSubjects(selectedItem);
  }

  function handleSubjectChange(selectedItem: any) {
    setSelectedSubject(selectedItem);
    setSelectedDomain(undefined);
    setSelectedStandard(undefined);
    setDomains([]);
    setStandards([]);
    bindDomains(selectedItem);
  }

  function handleDomainChange(selectedItem: any) {
    setSelectedDomain(selectedItem);
    setSelectedStandard(undefined);
    setStandards([]);
    bindStandards(selectedItem);
  }

  function handleStandardChange(selectedItem: any) {
    setSelectedStandard(selectedItem);
    if (selectedItem?.value) {
      setSlide({
        ...slide,
        standardId: parseInt(selectedItem.value) || 0,
      });
    }
  }

  function handleRedirection(name: string, id: number) {
    if (name && id) {
      navigate(`/${name}/edit/${id}`);
    } else {
      navigate(`/${name}`);
    }
  }

  const handleSave = async () => {
    if (!slide.title) {
      toast.error("Title is required");
      return;
    }
    if (!slide.link) {
      toast.error("Link is required");
      return;
    }
    if (!selectedStandard?.value) {
      toast.error("Standard is required");
      return;
    }

    setIsSaving(true);
    try {
      const slideData = {
        slideId: idToEdit && idToEdit > 0 ? idToEdit : 0,
        standardId: parseInt(selectedStandard.value) || 0,
        title: slide.title,
        description: slide.description,
        link: slide.link,
        isActive: slide.isActive ?? true,
        userId: props.profile?.userId,
      };
      const result = await saveSlide(slideData);
      toast.success("Slide saved successfully");

      if (!idToEdit || idToEdit === 0) {
        // New slide - navigate to edit page
        const newIdRaw: any = result?.data?.data ?? result?.data;
        const newId =
          typeof newIdRaw === "string" ? parseInt(newIdRaw, 10) : newIdRaw;
        if (newId) {
          navigate(constant.SLIDEEDIT.replace(":id", newId.toString()));
        } else {
          navigate(constant.SLIDES);
        }
      } else {
        navigate(constant.SLIDES);
      }
    } catch (error) {
      toast.error("Error saving slide");
    }
    setIsSaving(false);
  };

  const handleCancel = () => {
    navigate(constant.SLIDES);
  };

  const breadcrumbItems = () => {
    const items: Array<IBreadcrumb> = [
      { name: "Slides", url: constant.SLIDES },
      { name: id ? "Edit Slide" : "Add Slide", url: "" },
    ];

    if (slide.slideId && id) {
      items.push({
        name: "",
        url: "",
        objectId: id?.toString(),
      });
    }

    return items;
  };

  const selectTheme = (theme) => ({
    ...theme,
    colors: {
      ...theme.colors,
      primary25: "#B3B3B3",
      primary: "#041E42",
    },
  });

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      background:
        document.getElementsByClassName("dark")?.length > 0
          ? "#1A1C23"
          : "white",
      borderColor:
        document.getElementsByClassName("dark")?.length > 0
          ? "#6B7280"
          : "#D1D5DB",
      borderWidth: "1px",
      "&:hover": {
        borderColor:
          state.isFocused && document.getElementsByClassName("dark")?.length > 0
            ? "#6b7280"
            : state.isFocused
            ? "#4c5563"
            : "",
      },
    }),
    singleValue: (base) => ({
      ...base,
      color:
        document.getElementsByClassName("dark")?.length > 0
          ? "white"
          : "#374051",
    }),
    input: (base) => ({
      ...base,
      color: document.getElementsByClassName("dark")?.length > 0 ? "white" : "",
    }),
  };

  // Convert slide URL to embeddable format
  const getSlideEmbedUrl = (url: string): string | null => {
    if (!url) return null;

    // Google Slides URL patterns
    // https://docs.google.com/presentation/d/PRESENTATION_ID/edit
    // https://docs.google.com/presentation/d/PRESENTATION_ID/pub
    const googleSlidesPattern =
      /https:\/\/docs\.google\.com\/presentation\/d\/([a-zA-Z0-9_-]+)/;
    const googleMatch = url.match(googleSlidesPattern);
    if (googleMatch) {
      const presentationId = googleMatch[1];
      return `https://docs.google.com/presentation/d/${presentationId}/embed?start=false&loop=false&delayms=3000`;
    }

    // For other URLs, return as-is (will be embedded in iframe)
    // This works for many embeddable content types
    return url;
  };

  const embedUrl = getSlideEmbedUrl(slide.link);

  return (
    <AllowedTo
      perform={[Permissions.slide_create, Permissions.slide_modify]}
      no={() => <AccessDenied />}
    >
      {loading && <Loader />}
      <div className="flex-1 overflow-auto">
        <div className="h-auto min-h-half rounded-bl-3xl bg-site-teal px-8 py-8 pt-0 pb-14 overflow-y-auto mt-7">
          <div className="relative py-1">
            <div className="overlay-image-main pointer-events-none fixed left-0 top-0 w-screen h-screen z-20"></div>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-light text-gray-900">
                {slideToEdit?.slideId ? "Edit Slide" : "Add Slide"}
              </h3>
            </div>
            <Breadcrumb items={breadcrumbItems()} />
            <div className="mt-6 w-full">
              <div>
                <div>
                  <div className="flex space-x-8">
                    {/* Left Panel - Form */}
                    <div className="shadow border rounded-lg border-gray-300 mt-4 px-4 py-6 w-1/4">
                      <div>
                        {/* Title */}
                        <div className="grid grid-cols-1 mt-5">
                          <div className="text-sm">
                            <label className="font-medium text-gray-700">
                              Title
                            </label>
                          </div>
                          <div>
                            <input
                              type="text"
                              value={slide.title}
                              onChange={(e) =>
                                setSlide({ ...slide, title: e.target.value })
                              }
                              autoComplete="off"
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-color focus:border-primary-color dark:focus:border-gray-500 dark:focus:ring-gray-500 dark:bg-custom-black dark:text-white sm:text-sm"
                            />
                          </div>
                        </div>

                        {/* Content Area */}
                        <div className="grid grid-cols-1 mt-5">
                          <div className="text-sm col-span-1 flex items-center">
                            {selectedContent?.value ? (
                              <a
                                className="text-gray-700 text-sm underline"
                                onClick={() =>
                                  handleRedirection(
                                    "content-areas",
                                    parseInt(selectedContent?.value)
                                  )
                                }
                                href={`/content-areas/edit/${parseInt(
                                  selectedContent?.value
                                )}`}
                              >
                                Content Area
                              </a>
                            ) : (
                              <button className="text-gray-700 text-sm cursor-default">
                                Content Area
                              </button>
                            )}
                          </div>
                          <div className="mt-1">
                            <Select
                              value={selectedContent}
                              onChange={handleContentAreaChange}
                              options={contentAreas}
                              className="text-gray-900 cursor-default select-none relative py-2"
                              isSearchable={true}
                              theme={selectTheme}
                              styles={selectStyles}
                            />
                          </div>
                        </div>

                        {/* Subject */}
                        <div className="grid grid-cols-1 mt-5">
                          <div className="text-sm col-span-1 flex items-center">
                            {selectedSubject?.value ? (
                              <a
                                className="text-gray-700 text-sm underline"
                                onClick={() =>
                                  handleRedirection(
                                    "subjects",
                                    parseInt(selectedSubject?.value)
                                  )
                                }
                                href={`/subjects/edit/${parseInt(
                                  selectedSubject?.value
                                )}`}
                              >
                                Subject
                              </a>
                            ) : (
                              <button className="text-gray-700 text-sm cursor-default">
                                Subject
                              </button>
                            )}
                          </div>
                          <div className="mt-1">
                            <Select
                              value={selectedSubject}
                              onChange={handleSubjectChange}
                              options={subjects}
                              className="text-gray-900 cursor-default select-none relative py-2"
                              isSearchable={true}
                              theme={selectTheme}
                              styles={selectStyles}
                            />
                          </div>
                        </div>

                        {/* Domain */}
                        <div className="grid grid-cols-1 mt-5">
                          <div className="text-sm col-span-1 flex items-center">
                            {selectedDomain?.value ? (
                              <a
                                className="text-gray-700 text-sm underline"
                                onClick={() =>
                                  handleRedirection(
                                    "domains",
                                    parseInt(selectedDomain?.value)
                                  )
                                }
                                href={`/domains/edit/${parseInt(
                                  selectedDomain?.value
                                )}`}
                              >
                                Domain
                              </a>
                            ) : (
                              <button className="text-gray-700 text-sm cursor-default">
                                Domain
                              </button>
                            )}
                          </div>
                          <div className="mt-1">
                            <Select
                              value={selectedDomain}
                              onChange={handleDomainChange}
                              options={domains}
                              className="text-gray-900 cursor-default select-none relative py-2"
                              isSearchable={true}
                              theme={selectTheme}
                              styles={selectStyles}
                            />
                          </div>
                        </div>

                        {/* Standard */}
                        <div className="grid grid-cols-1 mt-5">
                          <div className="text-sm col-span-1 flex items-center">
                            {selectedStandard?.value ? (
                              <a
                                className="text-gray-700 text-sm underline"
                                onClick={() =>
                                  handleRedirection(
                                    "standards",
                                    parseInt(selectedStandard?.value.toString())
                                  )
                                }
                                href={`/standards/edit/${parseInt(
                                  selectedStandard?.value.toString()
                                )}`}
                              >
                                Standard
                              </a>
                            ) : (
                              <button className="text-gray-700 text-sm cursor-default">
                                Standard
                              </button>
                            )}
                          </div>
                          <div className="mt-1">
                            <Select
                              value={selectedStandard}
                              onChange={handleStandardChange}
                              options={standards}
                              className="text-gray-900 cursor-default select-none relative py-2"
                              isSearchable={true}
                              theme={selectTheme}
                              styles={selectStyles}
                            />
                          </div>
                        </div>

                        {/* Link */}
                        <div className="grid grid-cols-1 mt-5">
                          <div className="text-sm">
                            <label className="font-medium text-gray-700">
                              Slide Link
                            </label>
                          </div>
                          <div>
                            <input
                              type="url"
                              value={slide.link}
                              onChange={(e) =>
                                setSlide({ ...slide, link: e.target.value })
                              }
                              placeholder="https://docs.google.com/presentation/d/..."
                              autoComplete="off"
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-color focus:border-primary-color dark:focus:border-gray-500 dark:focus:ring-gray-500 dark:bg-custom-black dark:text-white sm:text-sm"
                            />
                          </div>
                        </div>

                        {/* Description */}
                        <div className="grid grid-cols-1 mt-5">
                          <div className="text-sm">
                            <label className="font-medium text-gray-700">
                              Description
                            </label>
                          </div>
                          <div>
                            <textarea
                              rows={4}
                              value={slide.description}
                              onChange={(e) =>
                                setSlide({
                                  ...slide,
                                  description: e.target.value,
                                })
                              }
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-color focus:border-primary-color dark:focus:border-gray-500 dark:focus:ring-gray-500 dark:bg-custom-black dark:text-white sm:text-sm"
                            />
                          </div>
                        </div>

                        {/* Active Checkbox */}
                        <div className="grid grid-cols-1 mt-5">
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={slide.isActive}
                              onChange={(e) =>
                                setSlide({
                                  ...slide,
                                  isActive: e.target.checked,
                                })
                              }
                              className="rounded border-gray-300 text-primary-color shadow-sm focus:border-primary-color focus:ring focus:ring-primary-color focus:ring-opacity-50"
                            />
                            <span className="ml-2 text-sm text-gray-700">
                              Active
                            </span>
                          </label>
                        </div>

                        {/* Buttons */}
                        <div className="mt-6 flex justify-end space-x-3">
                          <button
                            type="button"
                            onClick={handleCancel}
                            className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color"
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            onClick={handleSave}
                            disabled={isSaving}
                            className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-color hover:bg-secondary-color focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color disabled:opacity-50"
                          >
                            {isSaving ? "Saving..." : "Save"}
                          </button>
                        </div>
                      </div>
                    </div>
                    {/* Right Panel - Preview and Tabs */}
                    <div className="w-3/4 mt-4">
                      {/* Add mode - show preview only */}
                      {!id && (
                        <div className="flex flex-col">
                          <h2 className="text-lg font-medium text-gray-900 mb-4">
                            Slide Preview
                          </h2>
                          {embedUrl ? (
                            <div className="shadow-lg bg-white border rounded-lg overflow-hidden">
                              <iframe
                                src={embedUrl}
                                title={slide.title || "Slide Preview"}
                                className="w-full border-none"
                                style={{ height: "500px" }}
                                allowFullScreen
                              />
                            </div>
                          ) : (
                            <div className="bg-gray-100 border border-gray-300 rounded-lg p-8 text-center text-gray-500">
                              <p>Enter a slide URL to see a preview</p>
                              <p className="text-sm mt-2">
                                Supports Google Slides and other embeddable URLs
                              </p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Edit mode - tabs with preview and standards */}
                      {id && slideToEdit && slideToEdit.slideId > 0 && (
                        <>
                          <div className="flex items-center justify-between bg-gray-100 w-full">
                            <div className="flex">
                              <button
                                type="button"
                                onClick={() => setActiveTab("SlideDetails")}
                              >
                                <h2
                                  className={
                                    activeTab === "SlideDetails"
                                      ? activeTabClass
                                      : inActiveTabClass
                                  }
                                >
                                  Slide Preview
                                </h2>
                              </button>
                              <button
                                type="button"
                                onClick={() => setActiveTab("Standards")}
                              >
                                <h2
                                  className={
                                    activeTab === "Standards"
                                      ? activeTabClass
                                      : inActiveTabClass
                                  }
                                >
                                  Standards
                                </h2>
                              </button>
                            </div>
                          </div>
                          {activeTab === "SlideDetails" && (
                            <div className="bg-white border border-gray-200">
                              {embedUrl ? (
                                <iframe
                                  src={embedUrl}
                                  title={slide.title || "Slide Preview"}
                                  className="w-full border-none"
                                  style={{ height: "500px" }}
                                  allowFullScreen
                                />
                              ) : (
                                <div className="p-8 text-center text-gray-500">
                                  <p>No slide URL provided</p>
                                </div>
                              )}
                              {slide.description && (
                                <div className="p-4 border-t border-gray-200">
                                  <p className="text-sm text-gray-600">
                                    <strong>Description:</strong>{" "}
                                    {slide.description}
                                  </p>
                                </div>
                              )}
                            </div>
                          )}
                          {activeTab === "Standards" && (
                            <SlideStandards slideData={slide} />
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AllowedTo>
  );
};

const mapStateToProps = (state: any) => {
  return {
    profile: state.profile,
  };
};

export default connect(mapStateToProps)(ManageSlide);
